import React, { useState, useEffect, useRef } from 'react';
import './api-keys.styles.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlusCircle, faKey, faCopy, faCheck, faRefresh,
  faEyeSlash, faShield, faCircleInfo, faTrash
} from '@fortawesome/free-solid-svg-icons';
import { getCollectionFromDb } from '@/utils/getDataFromDb';
import { UseCloudFunction } from '@/utils/firebase.utils';
import { format, set } from 'date-fns';
import { useSelector } from 'react-redux';

const STEP = { IDLE: 'idle', FORM: 'form', REVEALING: 'revealing' };

const ApiKeys = () => {
  const userProfile = useSelector(state => state.userData.userProfile);
  const [keys, setKeys]                   = useState(null);
  const [step, setStep]                   = useState(STEP.IDLE);
  const [formData, setFormData]           = useState({ userName: '', label: '' });
  const [regenTarget, setRegenTarget]     = useState(null);
  const [generatedKey, setGeneratedKey]   = useState(null);
  const [keyCopied, setKeyCopied]         = useState(false);
  const [loading, setLoading]             = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [notification, setNotification]   = useState(null);
  const listenerRef = useRef(null);
  const keysRef = useRef({});

  useEffect(() => {
    getCollectionFromDb({
      path: 'api_credentials',
      callback: (data) => {
        keysRef.current = {
          ...keysRef.current,
          ...{[data.id]: data}
        }
        setKeys(keysRef.current);
      },
      handleListener: (unsub) => { listenerRef.current = unsub; }
    });

    const timeout = setTimeout(() => {
      if (!keysRef.current || Object.keys(keysRef.current).length === 0) {
        setKeys({});
        clearTimeout(timeout);
      }
    }, 3000);
    return () => { if (listenerRef.current) listenerRef.current(); };
  }, []);

  const notify = (msg, type = 'success') => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const resetForm = () => {
    setStep(STEP.IDLE);
    setFormData({ userName: '', label: '' });
    setRegenTarget(null);
    setGeneratedKey(null);
    setKeyCopied(false);
  };

  const submitKey = async () => {
    setLoading(true);
    const payload = regenTarget
      ? 
      { 
        userName: regenTarget, 
        isRotation: true 
      }
      : 
      { 
        userName: formData.userName.trim(), 
        label: formData.label.trim() || formData.userName.trim(), 
        isNewKey: true 
      };
    payload.userCstKey = userProfile.cst_key;
    const result = await UseCloudFunction(
      'saveApiKey', 
      payload
    );
    setLoading(false);

    console.log(result);
    if (!result || result.error) {
      notify(result?.error || 'Failed to generate key.', 'error');
      return;
    }
    setGeneratedKey(result.plainKey);
    setStep(STEP.REVEALING);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedKey);
    setKeyCopied(true);
  };

  const handleConfirmSaved = () => {
    const msg = regenTarget ? 'API key regenerated.' : 'API key created.';
    resetForm();
    notify(msg);
  };

  const handleDelete = async (userName) => {
    setConfirmDelete(null);
    setLoading(true);
    const result = await UseCloudFunction(
      'saveApiKey', 
      { 
        userName: userName, 
        isDeactivation: true, 
        userCstKey: userProfile.cst_key 
      }
    );
    setLoading(false);
    if (result?.error) {
      notify(result.error, 'error');
    } else {
      notify('API key deleted.');
    }
  };

  const formatDate = (ts) => {
    if (!ts) return '—';
    try { return format(ts.toDate ? ts.toDate() : new Date(ts), 'MMM d, yyyy'); }
    catch { return '—'; }
  };

  return (
    <div className='api-keys-container'>

      <div className='section-title with-icon'>
        Manage API Keys
        <div className='multiple-icons'>
          {step === STEP.IDLE && (
            <button
              className='g-button text'
              title='Create New API Key'
              onClick={() => setStep(STEP.FORM)}>
              Create New Key
              <FontAwesomeIcon icon={faPlusCircle} className='icon' />
            </button>
          )}
        </div>
      </div>
      <hr />

      {notification && (
        <div className={`ak-notification ${notification.type}`}>
          {notification.msg}
        </div>
      )}

      {/* ── FORM: create or regenerate ─────────────────────────────────── */}
      {step === STEP.FORM && (
        <div className='g-card ak-form-card'>
          <div className='ak-card-header'>
            <FontAwesomeIcon icon={faKey} />
            <span>{regenTarget ? `Regenerate Key — ${regenTarget}` : 'New API Key'}</span>
          </div>
          <div className='g-space-1'></div>

          {regenTarget ? (
            <div className='ak-warning-banner'>
              <FontAwesomeIcon icon={faCircleInfo} />
              <span>
                Regenerating will immediately invalidate the current key. Any system
                using it will lose access until updated with the new key.
              </span>
            </div>
          ) : (
            <>
              <div className='field'>
                <label>
                  Username <span className='ak-required'>*</span>
                </label>
                <input
                  type='text'
                  placeholder='e.g. my-website'
                  value={formData.userName || ''}
                  onChange={e =>
                    setFormData(f => ({
                      ...f,
                      userName: e.target.value.toLowerCase().replace(/\s+/g, '-')
                    }))
                  }
                  disabled={loading}
                />
                <span className='meta'>
                  Unique identifier used for authentication. Lowercase, hyphens allowed.
                </span>
              </div>
              <div className='field'>
                <label>Label</label>
                <input
                  type='text'
                  placeholder='e.g. My Website Integration'
                  value={formData.label || ''}
                  onChange={e => setFormData(f => ({ ...f, label: e.target.value }))}
                  disabled={loading}
                />
                <span className='meta'>Optional friendly name for your reference.</span>
              </div>
            </>
          )}

          <div className='ak-form-actions'>
            <button className='g-button' onClick={resetForm} disabled={loading}>
              Cancel
            </button>
            <button
              className='g-button primary'
              onClick={submitKey}
              disabled={(!regenTarget && !formData.userName.trim()) || loading}>
              {loading ? 'Generating…' : regenTarget ? 'Generate New Key' : 'Generate Key'}
            </button>
          </div>
        </div>
      )}

      {/* ── KEY REVEAL ─────────────────────────────────────────────────── */}
      {step === STEP.REVEALING && generatedKey && (
        <div className='g-card ak-reveal-card'>
          <div className='ak-card-header'>
            <FontAwesomeIcon icon={faShield} />
            <span>Your New API Key</span>
          </div>
          <div className='ak-warning-banner amber'>
            <FontAwesomeIcon icon={faCircleInfo} />
            <span>
              This key will only be shown <strong>once</strong> and cannot be
              retrieved after you leave this screen. Copy it and store it somewhere
              safe now.
            </span>
          </div>
          <div className='ak-key-display'>
            <code className='ak-key-value'>{generatedKey}</code>
            <button
              className={`g-button ak-copy-btn ${keyCopied ? 'copied' : ''}`}
              title={keyCopied ? 'Copied!' : 'Copy to clipboard'}
              onClick={handleCopy}>
              <FontAwesomeIcon icon={keyCopied ? faCheck : faCopy} />
              {keyCopied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <div className='ak-form-actions'>
            <span className='meta ak-copy-hint'>
              {!keyCopied && 'Copy the key above before continuing.'}
            </span>
            <button
              className='g-button primary'
              disabled={!keyCopied}
              title={!keyCopied ? 'Copy the key first' : ''}
              onClick={handleConfirmSaved}>
              <FontAwesomeIcon icon={faCheck} />
              I've Saved This Key
            </button>
          </div>
        </div>
      )}

      {/* ── KEY LIST ───────────────────────────────────────────────────── */}
      {step === STEP.IDLE && (
        <div className='ak-keys-list'>
          {keys === null ? (
            <p className='meta'>Loading…</p>
          ) : Object.keys(keys).length === 0 ? (
            <div className='ak-empty'>
              <FontAwesomeIcon icon={faKey} size='2x' />
              <p>No API keys yet. Create one to get started.</p>
            </div>
          ) : (
            Object.values(keys).map(key => (
              <div className='g-card ak-key-row' key={key.id}>
                <div className='ak-key-info'>
                  <div className='ak-key-label'>
                    {key.label || key.userName}
                    <span className={`ak-badge ${key.status === 'active' ? 'active' : 'inactive'}`}>
                      {key.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className='meta ak-key-meta'>
                    <span>Username: <strong>{key.userName}</strong></span>
                    {key.ts_created && <span>Created: {formatDate(key.ts_created)}</span>}
                    {key.ts         && <span>Last rotated: {formatDate(key.ts)}</span>}
                  </div>
                  <div className='ak-key-masked'>
                    <FontAwesomeIcon icon={faEyeSlash} />
                    <code>{'•'.repeat(32)}</code>
                  </div>
                </div>
                <div className='ak-key-actions'>
                  <button
                    className='g-button text'
                    title='Generate a new key for this user'
                    onClick={() => { setRegenTarget(key.userName); setStep(STEP.FORM); }}
                    disabled={loading}>
                    <FontAwesomeIcon icon={faRefresh} />
                    Regenerate
                  </button>
                  <button
                    className='g-button text ak-delete-btn'
                    title='Delete this key'
                    onClick={() => setConfirmDelete(key.userName)}
                    disabled={loading}>
                    <FontAwesomeIcon icon={faTrash} />
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ── DELETE CONFIRM MODAL ───────────────────────────────────────── */}
      {confirmDelete && (
        <div className='ak-overlay'>
          <div className='ak-modal g-card'>
            <div className='ak-modal-title'>Delete API Key?</div>
            <p>
              Deleting the key for <strong>{confirmDelete}</strong> will immediately
              revoke access. This cannot be undone — you would need to create a new
              key if access is needed again.
            </p>
            <div className='ak-form-actions'>
              <button className='g-button' onClick={() => setConfirmDelete(null)}>
                Cancel
              </button>
              <button
                className='g-button primary ak-delete-confirm-btn'
                onClick={() => handleDelete(confirmDelete)}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ApiKeys;
